import { useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import type { FlightState } from "@b2bportal/core-flights";
import {
  type FlightCoreRawEvent,
  type FlightCoreEvent,
  FlightRawEventName,
  FlightEventName,
  CORE_FLIGHTS_HTS_EVENT_TRANSFORMER_HOOKS,
  commonTrackingProperties,
  getFlightShopTrackingProperties,
} from "@b2bportal/core-flights";
import type { FlightEntryEvent } from "@b2bportal/core-flights/src/tracking/events/amplitude/FlightEntryEvent";
import type { FlightEntryRawEvent } from "@b2bportal/core-flights/src/tracking/events/raw/FlightEntryRawEvent";
import type { UseInstantTransformEvent } from "@b2bportal/core-tracking";
import type { EventTransformerHooks } from "@b2bportal/core-tracking";
import { useEventTracking } from "@b2bportal/core-tracking";
import { Funnel } from "@b2bportal/core-types";
import { useTrackFunnelEntry } from "./hooks";

/**
 * Hook to get providers (tracking attributes + redux state) for flights transformers.
 */
function useProviders() {
  const { getAllAttributes } = useEventTracking();
  const reduxContext = useContext(ReactReduxContext);

  return {
    trackingAttributes: getAllAttributes(),
    reduxState: reduxContext.store.getState() as FlightState,
  };
}

/**
 * RBC-specific transformer for air_entry event.
 * Fires on every funnel transition (not just once per session).
 */
export const useRbcFlightEnteredTransformer: UseInstantTransformEvent<
  FlightEntryRawEvent,
  FlightEntryEvent
> = () => {
  const shouldTrack = useTrackFunnelEntry(Funnel.Air);
  const providers = useProviders();

  return useCallback(
    (event) => {
      if (!shouldTrack) {
        return undefined;
      }

      return {
        name: FlightEventName.air_entry,
        properties: {
          ...commonTrackingProperties,
          ...getFlightShopTrackingProperties(providers.reduxState),
          ...event.properties,
        },
      };
    },
    [shouldTrack, providers.reduxState]
  );
};

/**
 * RBC flights event transformer hooks.
 */
export const RBC_FLIGHTS_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  FlightCoreRawEvent,
  FlightCoreEvent
> = {
  ...CORE_FLIGHTS_HTS_EVENT_TRANSFORMER_HOOKS,
  [FlightRawEventName.EnteredFlight]: [useRbcFlightEnteredTransformer],
};
